import React, { useEffect, useMemo, useState } from 'react';
import {
  ControlBar,
  Geometry,
  LayerSwitcher,
  Map,
  ResetPosition,
  ScaleLine,
  useMapActions,
  View,
  Zoom,
} from '@tq/web-maps';
import { Shape } from '@tq/web-maps/dist/react-ol/components/Shape';
import { drawStyle } from '@tq/web-maps/dist/styles/Map.styles';
import { ActiveLayer, MapWithPolygonMapProps, StyledControlBar } from '.';
import { LayerToggle } from './LayerToggle';
import { EMPTY_LAYER_DATA } from './empty-layer-data';
import {
  maxZoom,
  layerPadding,
  mapReference,
  satelliteMapReference,
  maxZoomSatellite,
} from './constants';
import { tqc } from '@tq/ui-components';

//This component is being used only in TQ theme at the moment
export const MapWithPolygon: React.FC<MapWithPolygonMapProps> = ({
  geoJson,
  displayLayerToggle,
  mapHeight = '480px',
}) => {
  const [activeLayer, setActiveLayer] = useState<ActiveLayer>(ActiveLayer.MAP);
  const isActiveLayerMAP = activeLayer === ActiveLayer.MAP;

  const { fitFeature } = useMapActions();

  useEffect(() => {
    const polygonLayerId = geoJson?.features?.[0]?.id;
    if (polygonLayerId) {
      fitFeature(polygonLayerId, {
        padding: layerPadding,
        maxZoom: isActiveLayerMAP ? maxZoom : maxZoomSatellite,
      });
    }
  }, [geoJson, fitFeature, isActiveLayerMAP]);

  const applicationPolygonLayer = (
    <Geometry
      key='application-polygon-layer'
      id='application-polygon-layer'
      label='Application polygon Layer'
      data={geoJson ?? EMPTY_LAYER_DATA}
      drawShape={Shape.Polygon}
      minZoom={0}
      maxZoom={maxZoom}
      zIndex={101}
      style={drawStyle}
    />
  );

  // when user selects a view, we send two reference objects,
  // one to switch off the deselected map layer
  // and a second one to enable the selected map layer
  // e.g. if user selects map view, we send one object to hide satellite map layer
  // and a second object that displays the map layer
  const enabledLayers = useMemo(
    () => [
      {
        reference: isActiveLayerMAP ? mapReference : satelliteMapReference,
        display: true,
      },
      {
        reference: isActiveLayerMAP ? satelliteMapReference : mapReference,
        display: false,
      },
    ],
    [isActiveLayerMAP]
  );

  return (
    <tqc.div
      style={{ width: '100%', height: mapHeight, position: 'relative' }}
      data-testid='map-component'
    >
      <Map>
        <View />
        <StyledControlBar id='styled-control-bar'>
          <ControlBar>
            <tqc.div
              backgroundColor='white'
              position='absolute'
              bottom='10px'
              left='0'
              right='0'
              height='62px'
              width='90%'
              marginInline='auto'
              borderRadius='4px'
              boxShadow='0px 2px 5px rgba(0,0,0,0.2)'
            >
              <LayerSwitcher enableLayers={enabledLayers} hidden>
                {[applicationPolygonLayer]}
              </LayerSwitcher>

              <ResetPosition />
              <Zoom />

              <ScaleLine />
              {displayLayerToggle && (
                <LayerToggle activeLayer={activeLayer} handleOnChange={setActiveLayer} />
              )}
            </tqc.div>
          </ControlBar>
        </StyledControlBar>
      </Map>
    </tqc.div>
  );
};
